// src/services/recurrence.ts
//
// Shared recurrence helpers — the single source of truth for recurrence pattern
// constants, formatting, and RecurrenceRule <-> form-state conversion. Used by
// RecurringTasksPage, RecurrenceFields, and TaskFormModal so the recurrence
// logic lives in exactly one place.

import type { RecurrenceRule, RecurrenceFrequency } from '../types/api';

export const FREQUENCY_CONFIG: Record<RecurrenceFrequency, { label: string; plural: string }> = {
  daily: { label: 'Daily', plural: 'days' },
  weekly: { label: 'Weekly', plural: 'weeks' },
  monthly: { label: 'Monthly', plural: 'months' },
  yearly: { label: 'Yearly', plural: 'years' },
};

export const FREQUENCIES: RecurrenceFrequency[] = ['daily', 'weekly', 'monthly', 'yearly'];

export const DAYS_OF_WEEK: { value: number; label: string }[] = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

/** The editable recurrence pattern (no startAt/timezone — those are scheduling, owned by the parent form). */
export interface RecurrencePatternState {
  frequency: RecurrenceFrequency;
  interval: number;
  daysOfWeek: number[];
  dayOfMonth: number;
}

export const defaultRecurrencePattern: RecurrencePatternState = {
  frequency: 'weekly',
  interval: 1,
  daysOfWeek: [1], // Monday
  dayOfMonth: 1,
};

/** Build the API RecurrenceRule from form state, including only fields relevant to the frequency. */
export function buildRecurrenceRule(state: RecurrencePatternState): RecurrenceRule {
  const rule: RecurrenceRule = { frequency: state.frequency, interval: state.interval };
  if (state.frequency === 'weekly') rule.daysOfWeek = state.daysOfWeek;
  else if (state.frequency === 'monthly') rule.dayOfMonth = state.dayOfMonth;
  return rule;
}

/** Inverse of buildRecurrenceRule — hydrate form state from an existing rule. */
export function recurrenceRuleToState(rule: RecurrenceRule): RecurrencePatternState {
  return {
    frequency: rule.frequency,
    interval: rule.interval,
    daysOfWeek: rule.daysOfWeek ?? [...defaultRecurrencePattern.daysOfWeek],
    dayOfMonth: rule.dayOfMonth ?? defaultRecurrencePattern.dayOfMonth,
  };
}

/** Human-readable summary, e.g. "Every 2 weeks on Mon, Wed". */
export function formatRecurrence(rule: RecurrenceRule): string {
  const freq = FREQUENCY_CONFIG[rule.frequency];
  const interval = rule.interval > 1 ? `Every ${rule.interval} ${freq.plural}` : freq.label;

  if (rule.frequency === 'weekly' && rule.daysOfWeek?.length) {
    const days = rule.daysOfWeek.map((d) => DAYS_OF_WEEK[d]?.label).join(', ');
    return `${interval} on ${days}`;
  }

  if (rule.frequency === 'monthly' && rule.dayOfMonth) {
    return `${interval} on day ${rule.dayOfMonth}`;
  }

  return interval;
}
